import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  ListBulletIcon,
  UserIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { NafList, CreateNafListData, UpdateNafListData } from '../../types/nafList';
import { NafCode } from '../nouveauxAdherents/NafCodeSelector';
import NafListModal from './NafListModal';
import { 
  getNafLists, 
  createNafList, 
  updateNafList, 
  deleteNafList 
} from '../../services/nafListService';
import { showSuccessToast, showErrorToast } from '../../utils/toastUtils';

interface NafListManagementProps {
  nafCodes: NafCode[];
  isLoadingNafCodes?: boolean;
}

const NafListManagement: React.FC<NafListManagementProps> = ({
  nafCodes,
  isLoadingNafCodes = false,
}) => {
  const [nafLists, setNafLists] = useState<NafList[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingList, setEditingList] = useState<NafList | null>(null);
  const [deletingListId, setDeletingListId] = useState<number | null>(null);

  // Fetch NAF lists on component mount
  useEffect(() => {
    fetchNafLists();
  }, []);

  const fetchNafLists = async () => {
    setIsLoading(true);
    try {
      const lists = await getNafLists();
      setNafLists(lists);
    } catch (error) {
      console.error('Error fetching NAF lists:', error);
      showErrorToast('Erreur lors du chargement des listes NAF', 'fetch-naf-lists-error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateList = () => {
    setEditingList(null);
    setIsModalOpen(true);
  };

  const handleEditList = (list: NafList) => {
    setEditingList(list);
    setIsModalOpen(true);
  };

  const handleSaveList = async (data: CreateNafListData | UpdateNafListData) => {
    try {
      if (editingList) {
        // Update existing list
        await updateNafList(editingList.id, data as UpdateNafListData);
        showSuccessToast('Liste NAF modifiée avec succès', 'update-naf-list-success');
      } else {
        // Create new list
        await createNafList(data as CreateNafListData);
        showSuccessToast('Liste NAF créée avec succès', 'create-naf-list-success');
      }
      
      // Refresh the list
      await fetchNafLists();
    } catch (error: any) {
      console.error('Error saving NAF list:', error);
      const errorMessage = error.response?.data?.message || 'Erreur lors de l\'enregistrement de la liste NAF';
      showErrorToast(errorMessage, 'save-naf-list-error');
      throw error; // Re-throw to prevent modal from closing
    }
  };

  const handleDeleteList = async (id: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette liste NAF ?')) {
      return;
    }

    setDeletingListId(id);
    try {
      await deleteNafList(id);
      showSuccessToast('Liste NAF supprimée avec succès', 'delete-naf-list-success');
      await fetchNafLists();
    } catch (error: any) {
      console.error('Error deleting NAF list:', error);
      const errorMessage = error.response?.data?.message || 'Erreur lors de la suppression de la liste NAF';
      showErrorToast(errorMessage, 'delete-naf-list-error');
    } finally {
      setDeletingListId(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getNafCodeLabel = (code: string): string => {
    const nafCode = nafCodes.find(naf => naf.code === code);
    return nafCode ? nafCode.intitule : 'Code inconnu';
  };

  if (isLoading) {
    return (
      <div className="bg-white shadow-md rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            <div className="h-4 bg-gray-200 rounded w-4/6"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-md rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Gestion des listes NAF</h2>
        <button
          onClick={handleCreateList}
          disabled={isLoadingNafCodes}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Créer une liste
        </button>
      </div>

      {nafLists.length === 0 ? (
        <div className="text-center py-12">
          <ListBulletIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune liste NAF</h3>
          <p className="mt-1 text-sm text-gray-500">
            Commencez par créer votre première liste de codes NAF prédéfinis.
          </p>
          <div className="mt-6">
            <button
              onClick={handleCreateList}
              disabled={isLoadingNafCodes}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Créer une liste NAF
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {nafLists.map((list) => (
            <div
              key={list.id}
              className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {list.name}
                  </h3>
                  
                  {list.description && (
                    <p className="text-sm text-gray-600 mb-3">
                      {list.description}
                    </p>
                  )}

                  <div className="flex items-center space-x-4 text-xs text-gray-500 mb-3">
                    <div className="flex items-center">
                      <ListBulletIcon className="h-4 w-4 mr-1" />
                      {list.nafCodes.length} code{list.nafCodes.length > 1 ? 's' : ''} NAF
                    </div>
                    
                    {list.creator && (
                      <div className="flex items-center">
                        <UserIcon className="h-4 w-4 mr-1" />
                        {list.creator.firstName} {list.creator.lastName}
                      </div>
                    )}
                    
                    <div className="flex items-center">
                      <CalendarIcon className="h-4 w-4 mr-1" />
                      {formatDate(list.createdAt)}
                    </div>
                  </div>

                  {/* NAF codes preview */}
                  <div className="flex flex-wrap gap-1">
                    {list.nafCodes.slice(0, 3).map((code) => (
                      <span
                        key={code}
                        className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800"
                        title={getNafCodeLabel(code)}
                      >
                        {code}
                      </span>
                    ))}
                    {list.nafCodes.length > 3 && (
                      <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-600">
                        +{list.nafCodes.length - 3} autres
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center space-x-2 ml-4">
                  <button
                    onClick={() => handleEditList(list)}
                    disabled={isLoadingNafCodes}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                    title="Modifier la liste"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  
                  <button
                    onClick={() => handleDeleteList(list.id)}
                    disabled={deletingListId === list.id}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
                    title="Supprimer la liste"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal for creating/editing NAF lists */}
      <NafListModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveList}
        nafList={editingList}
        nafCodes={nafCodes}
        isLoading={isLoadingNafCodes}
      />
    </div>
  );
};

export default NafListManagement; 